import React, { useState, useEffect } from "react";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import Sidebar from "../../../components/layout/Sidebar";
import { IoSearch } from "react-icons/io5";
import axios from "axios";
import { BASE_URL } from "../../../env";
import { MdModeEdit, MdDelete } from "react-icons/md";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAdminAuth } from "../../auth/AdminAuthProvider";
import { useNavigate } from "react-router-dom";
import { IoMdAdd } from "react-icons/io";
import { IoEyeSharp } from "react-icons/io5";
import CustomPagination from "../../../components/ui/CustomPagination";
import ConfirmationModal from "../../../components/ui/ConfirmationModal";

const FundingAgencyList = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const { openSidebarToggle, toggleSidebar } = useAdminAuth();
  const [fundingAgencies, setFundingAgencies] = useState([]);
  const [filteredFundingAgencies, setFilteredFundingAgencies] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [fundingAgenciesPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchFundingAgencies();
  }, []);

  const fetchFundingAgencies = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/get/fundingagency`);
      const agencies = response.data.fundingAgencies || [];
      setFundingAgencies(agencies);
      setFilteredFundingAgencies(agencies);
    } catch (error) {
      console.error("Error fetching funding agencies:", error);
      toast.error("Failed to load funding agencies");
    }
  };

  useEffect(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      setFilteredFundingAgencies(fundingAgencies);
    } else {
      const filtered = fundingAgencies.filter((agency) => {
        const name = (agency.agencyName || agency.name || "").toLowerCase();
        const code = (agency.fundingAgencyCode || "").toLowerCase();
        const type = (agency.bcOrOwn || "").toLowerCase();
        return name.includes(query) || code.includes(query) || type.includes(query);
      });
      setFilteredFundingAgencies(filtered);
    }
    setCurrentPage(1);
  }, [searchQuery, fundingAgencies]);

  const OpenSidebar = () => {
    toggleSidebar();
  };

  const handleAddFundingAgencyClick = () => {
    navigate("/addEditFundingAgency", {
      state: { type: "Add", entity: "fundingAgencies" },
    });
  };

  const handleEditFundingAgency = (agency, mode = "Edit") => {
    navigate("/addEditFundingAgency", {
      state: { type: mode, id: agency.id, entity: "fundingAgencies" },
    });
  };

  const handleDeleteClick = (agencyId) => {
    setSelectedAgencyId(agencyId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedAgencyId) return;
    try {
      const response = await axios.post(`${BASE_URL}/api/delDetails`, {
        model: "funding_agencies",
        where: { id: selectedAgencyId },
      });

      if (response.status === 200) {
        toast.success("Funding agency deleted successfully!");
        fetchFundingAgencies();
      } else {
        toast.error("Failed to delete funding agency.");
      }
    } catch (error) {
      console.error("Error deleting funding agency:", error);
      toast.error(
        error.response?.data?.error || "An error occurred while deleting."
      );
    } finally {
      setShowDeleteModal(false);
      setSelectedAgencyId(null);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedAgencyId(null);
  };

  const indexOfLastFundingAgency = currentPage * fundingAgenciesPerPage;
  const indexOfFirstFundingAgency = indexOfLastFundingAgency - fundingAgenciesPerPage;
  const currentFundingAgencies = filteredFundingAgencies.slice(
    indexOfFirstFundingAgency,
    indexOfLastFundingAgency
  );

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const totalPages = Math.ceil(filteredFundingAgencies.length / fundingAgenciesPerPage);

  return (
    <>
      <section className="master-layout">
        <div className="master-sidebar-wrapper">
          <Sidebar
            openSidebarToggle={openSidebarToggle}
            OpenSidebar={OpenSidebar}
          />
        </div>

        <div className="master-content-wrapper">
          <div className="master-header-wrapper">
            <DashboardHeader OpenSidebar={OpenSidebar} />
          </div>

          <div className="master-card-container">
            {/* Header & Search Bar */}
            <div className="master-search-section inline-header">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <h1 className="TableHeaderText m-0">Funding Agencies</h1>
                <div className="search-input-wrapper">
                  <input
                    type="text"
                    placeholder="Search by name, code or type..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                  <span className="search-icon">
                    <IoSearch />
                  </span>
                </div>
              </div>

              <button
                className="btn btn-addCredential m-0"
                onClick={handleAddFundingAgencyClick}
              >
                <IoMdAdd />
                Add Funding Agency
              </button>
            </div>

            {/* List Section */}
            <div className="master-list-section">
              <div className="master-table-wrapper table-responsive">
                <table className="mx-auto table table-striped table-list">
                  <thead>
                    <tr>
                      <th style={{ width: "70px" }}>No</th>
                      <th>Funding Agency Code</th>
                      <th>Agency Name</th>
                      <th>BC / OWN</th>
                      <th style={{ width: "160px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentFundingAgencies.length > 0 ? (
                      currentFundingAgencies.map((agency, index) => (
                        <tr key={agency.id}>
                          <td>{index + indexOfFirstFundingAgency + 1}</td>
                          <td>{agency.fundingAgencyCode || "-"}</td>
                          <td className="fw-semibold">
                            {agency.agencyName || agency.name || "-"}
                          </td>
                          <td>{agency.bcOrOwn || "-"}</td>
                          <td>
                            <div className="d-flex justify-content-center align-items-center gap-2">
                              <button
                                className="btn btn-view"
                                title="View"
                                onClick={() => handleEditFundingAgency(agency, "View")}
                              >
                                <IoEyeSharp />
                              </button>
                              <button
                                className="btn btn-edit"
                                title="Edit"
                                onClick={() => handleEditFundingAgency(agency, "Edit")}
                              >
                                <MdModeEdit />
                              </button>
                              {!agency.hasData && (
                                <button
                                  className="btn btn-delete"
                                  title="Delete"
                                  onClick={() => handleDeleteClick(agency.id)}
                                >
                                  <MdDelete />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center py-4">
                          No funding agencies found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Central Custom Pagination */}
              <div className="master-pagination-wrapper">
                <CustomPagination
                  totalPages={totalPages}
                  currentPage={currentPage}
                  paginate={paginate}
                  totalItems={filteredFundingAgencies.length}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        handleConfirm={handleConfirmDelete}
        message="Are you sure you want to delete this funding agency?"
      />

      <ToastContainer position="top-center" />
    </>
  );
};

export default FundingAgencyList;